import { createEffect, createSignal, For, Show } from 'solid-js'
import Footer from '/web/shared/aifans/Footer'
import { AccountLayout } from '/web/shared/aifans/AccountLayout'
import { chatStore } from '/web/store'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { SubscriptionEvent, TokenTransaction } from '/common/types/aifans-schema'
import { useNavigate, useParams, A } from '@solidjs/router'
import { formatTransactionType } from '/web/shared/aifans/lib/transaction-util'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import { Link, Meta, Title } from '@solidjs/meta'

type TableData = SubscriptionEvent | TokenTransaction

export default function PaymentHistory() {
  const params = useParams()
  const navigate = useNavigate()
  const [tableData, setTableData] = createSignal<TableData[]>([])
  const [totalPages, setTotalPages] = createSignal(1)
  const pageSize = 10

  createEffect(async () => {
    const currentPage = Number(params.page) || 1
    await loadData(currentPage)
  })

  const loadData = async (page: number) => {
    const res = await aifansSubscriptionApi.getTransactions(page, pageSize)
    if (res.result) {
      setTableData(res.result.transactions)
      setTotalPages(Math.ceil(res.result.totalCount / pageSize))
    }
  }

  const changePage = (page: number) => {
    navigate(page === 1 ? '/payment-history' : `/payment-history/${page}`)
  }

  const characterFromId = (characterId: string) => {
    return chatStore().allChars.list.find((c) => c._id === characterId)
  }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString))
  }

  const thClass =
    'py-3.5 px-5 text-left font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.5)] lg:text-[13px] lg:py-[18px]'
  const tdClass = 'py-4 px-5 align-top font-rajdhani text-[14px] text-[rgba(255,255,255,0.78)] lg:text-[16px] lg:py-5'

  const statusPill = (status: string) => {
    const s = status.toLowerCase()
    if (s === 'completed' || s === 'success')
      return 'inline-block rounded-full px-3 py-1 text-[11px] font-rajdhani font-bold uppercase tracking-wider bg-[rgba(0,229,255,0.12)] text-[#00E5FF] border border-[rgba(0,229,255,0.25)] lg:text-xs lg:px-3.5 lg:py-1.5'
    if (s === 'refunded')
      return 'inline-block rounded-full px-3 py-1 text-[11px] font-rajdhani font-bold uppercase tracking-wider bg-[rgba(255,215,0,0.12)] text-[#FFD700] border border-[rgba(255,215,0,0.25)] lg:text-xs lg:px-3.5 lg:py-1.5'
    return 'inline-block rounded-full px-3 py-1 text-[11px] font-rajdhani font-bold uppercase tracking-wider bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] border border-[rgba(255,255,255,0.1)] lg:text-xs'
  }

  return (
    <>
      <Title>CosplayFans - Payment History</Title>
      <Meta name="og:title" content="CosplayFans - Payment History" />
      <Meta name="description" content="View your payment history on CosplayFans." />
      <Meta name="og:description" content="View your payment history on CosplayFans." />
      <Link rel="canonical" href={getCanonicalUrl('/payment-history/')} />

      <AccountLayout>
        <div class="mt-3 font-bebas text-[28px] sm:text-[38px] lg:text-[48px] tracking-[1.5px] leading-none text-white mb-4 sm:mt-0 sm:mb-5">Payment History</div>

        <div
          class="overflow-hidden rounded-2xl border border-[rgba(138,43,226,0.15)]"
          style={{ background: 'rgba(16,12,30,0.6)' }}
        >
          <Show
            when={tableData().length > 0}
            fallback={
              <div class="py-16 text-center">
                <p class="font-bebas text-xl text-[rgba(255,255,255,0.4)]">
                  No payment history available.
                </p>
                <p class="mt-2 font-nunito text-sm text-[rgba(255,255,255,0.3)]">
                  Your transactions will appear here once you make a purchase.
                </p>
              </div>
            }
          >
            {/* Desktop table */}
            <div class="hidden overflow-x-auto sm:block">
              <table class="w-full border-collapse">
                <thead>
                  <tr style={{ background: 'rgba(138,43,226,0.08)', 'border-bottom': '1px solid rgba(255,255,255,0.08)' }}>
                    <th class={thClass}>Date</th>
                    <th class={thClass}>Creator</th>
                    <th class={thClass}>Amount</th>
                    <th class={thClass}>Type</th>
                    <th class={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData().map((item, i) => {
                    const amount = item.amount ? `$${item.amount.toFixed(2)}` : '$0.00'
                    const isTokenPurchase = item.type === 'token_purchase'
                    const char = characterFromId(item.characterId!)
                    const typeName = isTokenPurchase ? 'Tokens' : formatTransactionType(item.type)

                    return (
                      <tr
                        style={{
                          'border-bottom': '1px solid rgba(255,255,255,0.06)',
                        }}
                        class="transition-colors hover:bg-[rgba(138,43,226,0.05)]"
                      >
                        <td class={tdClass}>{formatDate(item.date)}</td>
                        <td class={tdClass}>
                          <Show when={!isTokenPurchase && char} fallback={<span class="text-[rgba(255,255,255,0.3)]">—</span>}>
                            <A
                              href={getModelUrl(char!.categorySlug, char!.slug)}
                              class="hover:underline"
                            >
                              <div class="font-bebas text-[16px] tracking-[0.5px] text-white lg:text-[19px] lg:tracking-[0.6px]">{char!.name}</div>
                              {char!.realName && (
                                <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)] lg:mt-1 lg:text-[13px]">
                                  {char!.realName}
                                </div>
                              )}
                            </A>
                          </Show>
                        </td>
                        <td class={tdClass}>{amount}</td>
                        <td class={tdClass}>{typeName}</td>
                        <td class={tdClass}>
                          <span class={statusPill('Completed')}>Completed</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div class="flex flex-col gap-3 p-4 sm:hidden">
              {tableData().map((item) => {
                const amount = item.amount ? `$${item.amount.toFixed(2)}` : '$0.00'
                const isTokenPurchase = item.type === 'token_purchase'
                const char = characterFromId(item.characterId!)
                const typeName = isTokenPurchase ? 'Tokens' : formatTransactionType(item.type)

                return (
                  <div
                    class="rounded-xl border border-[rgba(255,255,255,0.06)] p-4"
                    style={{ background: 'rgba(255,255,255,0.02)' }}
                  >
                    <div class="mb-2 font-rajdhani text-xs text-[rgba(255,255,255,0.4)]">
                      {formatDate(item.date)}
                    </div>
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <Show when={!isTokenPurchase && char}>
                          <A href={getModelUrl(char!.categorySlug, char!.slug)} class="hover:underline">
                            <div class="font-bebas text-base text-white">{char!.name}</div>
                          </A>
                        </Show>
                        <div class="font-rajdhani text-xs text-[rgba(255,255,255,0.5)]">{typeName}</div>
                      </div>
                      <div class="text-right">
                        <div class="font-bebas text-base">{amount}</div>
                        <span class={statusPill('Completed')}>Completed</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            <Show when={totalPages() > 1}>
              <div class="flex justify-center gap-2 border-t border-[rgba(255,255,255,0.06)] p-4">
                <For each={Array.from({ length: totalPages() }, (_, i) => i + 1)}>
                  {(page) => (
                    <button
                      onClick={() => changePage(page)}
                      class={`rounded-lg px-3 py-1.5 font-rajdhani text-xs font-bold transition-colors ${
                        Number(params.page || '1') === page
                          ? 'bg-cf-primary text-white'
                          : 'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] hover:bg-[rgba(255,255,255,0.1)]'
                      }`}
                    >
                      {page}
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </Show>
        </div>
      </AccountLayout>
      <Footer />
    </>
  )
}
